import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { ProfessionalFilters } from '@/types/professional';

interface ProfessionalActiveFilterChipsProps {
  filters: ProfessionalFilters;
  onFilterRemove: (key: string, value: string) => void;
  onClearAll: () => void;
}

export function ProfessionalActiveFilterChips({ filters, onFilterRemove, onClearAll }: ProfessionalActiveFilterChipsProps) {
  const getActiveFilters = () => {
    const activeFilters: Array<{ key: string; value: string; label: string }> = [];

    // License verification status
    if (filters.licenseVerificationStatus && filters.licenseVerificationStatus !== 'all') {
      const statusLabels = {
        verified: 'License Verified',
        pending: 'Verification Pending',
        not_verified: 'Not Verified'
      };
      activeFilters.push({
        key: 'licenseVerificationStatus',
        value: filters.licenseVerificationStatus,
        label: statusLabels[filters.licenseVerificationStatus]
      });
    }

    // Professional role
    if (filters.professionalRole && filters.professionalRole !== 'all') {
      const roleLabels: Record<string, string> = {
        care_manager: 'Care Manager',
        lcsw: 'Licensed Clinical Social Worker',
        ot: 'Occupational Therapist',
        pt: 'Physical Therapist',
        aging_consultant: 'Aging Consultant / Geriatric Specialist',
        financial_planner: 'Financial Planner',
        home_health: 'Home Health / Senior Services',
        patient_advocate: 'Patient Advocate',
        elder_law: 'Elder Law Attorney',
        other: 'Other',
        // Back-compat keys
        caps: 'CAPS Specialist',
        home_modification: 'Home Modification',
        financial_planning: 'Financial Planner',
      };
      activeFilters.push({
        key: 'professionalRole',
        value: filters.professionalRole,
        label: roleLabels[filters.professionalRole] || filters.professionalRole
      });
    }

    // Specialties with human-readable labels
    const specialtyLabels: Record<string, string> = {
      // Geriatric Care Manager
      care_coordination: 'Care Coordination',
      dementia_care: 'Dementia Care',
      chronic_disease: 'Chronic Disease Management',
      family_mediation: 'Family Mediation',
      discharge_planning: 'Discharge Planning',
      // OT/PT
      falls_prevention: 'Falls Prevention',
      home_safety: 'Home Safety Assessment',
      mobility_training: 'Mobility Training',
      adaptive_equipment: 'Adaptive Equipment',
      balance_strength: 'Balance & Strength',
      adl: 'Activities of Daily Living',
      // CAPS Specialist
      bathroom_mods: 'Bathroom Modifications',
      kitchen_accessibility: 'Kitchen Accessibility',
      stairway_safety: 'Stairway Safety',
      lighting_visibility: 'Lighting & Visibility',
      universal_design: 'Universal Design',
      smart_home: 'Smart Home Integration',
      // Elder Law Attorney
      estate_planning: 'Estate Planning',
      medicaid_planning: 'Medicaid Planning',
      guardianship: 'Guardianship',
      ltc_planning: 'Long-Term Care Planning',
      veterans_benefits: 'Veterans Benefits',
      // Financial Advisor
      retirement_planning: 'Retirement Planning',
      ltc_insurance: 'Long-Term Care Insurance',
      medicare_planning: 'Medicare Planning',
      estate_preservation: 'Estate Preservation',
      tax_strategy: 'Tax Strategy',
      // Caregiver/Agency
      personal_care: 'Personal Care',
      companionship: 'Companionship',
      medication_reminders: 'Medication Reminders',
      meal_prep: 'Meal Preparation',
      transportation: 'Transportation',
      respite_care: 'Respite Care',
      // Social Worker
      resource_navigation: 'Resource Navigation',
      crisis_intervention: 'Crisis Intervention',
      family_counseling: 'Family Counseling',
      benefits_enrollment: 'Benefits Enrollment',
      care_transitions: 'Care Transitions',
      // Home Modification
      accessibility_ramps: 'Accessibility Ramps',
      grab_bars: 'Grab Bars & Rails',
      walk_in_tubs: 'Walk-in Tubs',
      widening_doorways: 'Widening Doorways',
      flooring_safety: 'Flooring Safety',
      threshold_removal: 'Threshold Removal',
      // Clinician
      wound_care: 'Wound Care',
      medication_management: 'Medication Management',
      health_monitoring: 'Health Monitoring',
      palliative_care: 'Palliative Care',
      telehealth: 'Telehealth',
      // Other
      wellness_programs: 'Wellness Programs',
      nutrition_counseling: 'Nutrition Counseling',
      senior_fitness: 'Senior Fitness',
      tech_assistance: 'Technology Assistance',
    };

    if (filters.specialties && filters.specialties.length > 0) {
      filters.specialties.forEach(specialty => {
        activeFilters.push({
          key: 'specialties',
          value: specialty,
          label: specialtyLabels[specialty] || specialty
        });
      });
    }

    // Consultation modes
    if (filters.consultationModes && filters.consultationModes.length > 0) {
      filters.consultationModes.forEach(mode => {
        const modeLabels = {
          in_person: 'In-Person',
          telehealth: 'Telehealth',
          both: 'Both'
        };
        activeFilters.push({
          key: 'consultationModes',
          value: mode,
          label: modeLabels[mode] || mode
        });
      });
    }

    // Insurance accepted
    if (filters.insuranceAccepted && filters.insuranceAccepted.length > 0) {
      filters.insuranceAccepted.forEach(insurance => {
        activeFilters.push({
          key: 'insuranceAccepted',
          value: insurance,
          label: insurance
        });
      });
    }

    // Experience years
    if (filters.experienceYearsMin !== undefined || filters.experienceYearsMax !== undefined) {
      const min = filters.experienceYearsMin || 0;
      const max = filters.experienceYearsMax || '50+';
      activeFilters.push({
        key: 'experienceYears',
        value: `${min}-${max}`,
        label: `${min}-${max} years experience`
      });
    }

    // Service radius
    if (filters.serviceRadius !== undefined) {
      activeFilters.push({
        key: 'serviceRadius',
        value: filters.serviceRadius.toString(),
        label: `${filters.serviceRadius} mile radius`
      });
    }

    // Telehealth available
    if (filters.telehealthAvailable) {
      activeFilters.push({
        key: 'telehealthAvailable',
        value: 'true',
        label: 'Telehealth Available'
      });
    }

    // Certification bodies
    if (filters.certificationBodies && filters.certificationBodies.length > 0) {
      filters.certificationBodies.forEach(cert => {
        activeFilters.push({
          key: 'certificationBodies',
          value: cert,
          label: cert
        });
      });
    }

    return activeFilters;
  };

  const activeFilters = getActiveFilters();

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2 mb-6">
      <span className="text-sm text-st_taupe mr-2">Active filters:</span>
      {activeFilters.map((filter, index) => (
        <Badge
          key={`${filter.key}-${filter.value}-${index}`}
          variant="secondary"
          className="gap-1 pr-1"
        >
          {filter.label}
          <Button
            variant="ghost"
            size="sm"
            className="h-auto p-0 w-4 h-4 hover:bg-transparent"
            onClick={() => {
              if (filter.key === 'experienceYears') {
                onFilterRemove('experienceYearsMin', '');
                onFilterRemove('experienceYearsMax', '');
              } else {
                onFilterRemove(filter.key, filter.value);
              }
            }}
          >
            <X className="w-3 h-3" />
          </Button>
        </Badge>
      ))}
      
      <Button
        variant="ghost"
        size="sm"
        onClick={onClearAll}
        className="text-xs text-st_taupe hover:text-foreground ml-2"
      >
        Clear all
      </Button>
    </div>
  );
}